import { UserType } from "@models/auth";
import {
  FeeAnalysisRowType,
  FeeAnalysisTableRow,
} from "@models/managed-accounts/entities/analysis";
import {
  MAAnalysisBreakdownDTO,
  MAAnalysisDataDTO,
  MAAnalysisSectionDTO,
  MAAnalysisTooltipDTO,
  ManagedAccountFeeAnalysisContainerDTO,
} from "@models/managed-accounts/entities/step/fee";
import { ManagedAccountFieldType } from "@models/managed-accounts/enums/fieldType";
import { ManagedAccountFieldTypeValue } from "@models/managed-accounts/enums/fieldTypeValue";
import { FeeExportDTO } from "@models/platform-analysis/entities/feeExport";
import {
  FeeDetailDTO,
  FeeSubProductDTO,
} from "@models/platform-analysis/entities/steps/fee";
import {
  PortfolioCompositionCellDTO,
  ProductRecommendationDTO,
} from "@models/platform-analysis/entities/steps/summary";
import { FeesDisplayStyle } from "@models/platform-analysis/enums/fee/displayStyle";
import { FeeCalculatorPortfolioLevel } from "@models/platform-analysis/enums/fee/portfolioLevel";
import {
  MSDefaultDateFormat,
  MSWorkSheetStyleConstants,
} from "@pages/managed-accounts/model-screener/constant";
import { flatManagedAccountFeeAnalysisData } from "@pages/managed-accounts/_id/steps/fee-analysis/util";
import { getAnalysisSummaryPortfolioDetails } from "@pages/platform-analysis-v2/_id/steps/summary/components/portfolio-details/utils";
import { sortBySubProductOrder } from "@pages/platform-analysis-v2/_id/utils";
import {
  displayCurrencyNumber,
  displayIntNumber,
  getStringFamilyMemberAccounts,
  isAllExpandEssentialSubProductsHidden,
  removeSubProductNameForSpecificProduct,
} from "@pages/platform-analysis/util";
import {
  FeeAnalysisLabelConstants,
  HeatMapTableTitleConstants,
  PortfolioFeesConstants,
} from "@pages/platform-analysis/_id/steps/fee/analysis/constants";
import { FeePortfolioDetailsConstants } from "@pages/platform-analysis/_id/steps/summary/constants";
import { parse } from "date-fns";
import {
  forEach,
  isEmpty,
  isNil,
  isEqual,
  isNull,
  isNumber,
  some,
} from "lodash";
import { CellObject, Range, utils, WorkSheet } from "xlsx-js-style";
import {
  DefaultCellHeight,
  exportCurrency,
  exportDate,
  exportDateTime,
  exportPercentage,
  fillBlueV2,
  fillLightBlue,
  fillLightYellow,
  fillYellow,
  TitleCellHeight,
  TotalCharacterInCell,
} from "./config";
import {
  ExcelLabelConstants,
  ExcelSubTitle,
  FeeEstimatesTableLabelConstants,
  FeeEstimatesTableLabelMap,
} from "./constants";
import { AnalysisFeePortfolioSetupType } from "@models/platform-analysis/enums/fee/portfolioSetupType";

class MSExcelDownloadStaticValues {
  adviserName = "";
  practiceName = "";
  firmName = "";
  ownerName = "";
  ownerType = "";
  isSalesUser = false;
  isAdviserUser = false;
  isFamilyLevel = false;
  isExcludePerformanceFees = false;
  isDollarMode = false;
  serialCode = "";
  familyGroupName = "";
  endColIndex = 0;
  endRowIndex = 0;
  workSheetRows: MSWorkSheetRows = [];
  workSheetMerges: Range[] = [];
  fillBlueRowIndexes: number[] = [];
  fillLightBlueRowIndexes: number[] = [];
  fillYellowRowIndexes: number[] = [];
  fillLightYellowRowIndexes: number[] = [];
  hiddenSubProductIndexMap: boolean[] = [];
  styleTextBoldRowIndexes: number[] = [];
  styleBorderTopRowIndexes: number[] = [];
  rowsHeightTrack: Map<number, number> = new Map();
  onlyOneColData = false;
  isExcludeSubTotal = false;
  productNameCellHeight: number = 0;
  productNameCells: CellObject[] = [];
  hasMainProduct = false;
  numOfItems = 0;
  productIdsMap: Record<string, string> = {};
  productRecommendations: Record<string, Partial<ProductRecommendationDTO>> =
    {};
}

type MSWorkSheetRow = (CellObject | string)[];
type MSWorkSheetRows = MSWorkSheetRow[];

let staticValues = new MSExcelDownloadStaticValues();

export const getWorksheetV2 = (
  data?: FeeExportDTO,
  viewMode: FeesDisplayStyle = FeesDisplayStyle.Percentage,
  isExcludePerformanceFees = false,
  isExcludeSubTotal = false,
): WorkSheet => {
  if (!data) return utils.aoa_to_sheet([]);
  setStaticData(data, viewMode, isExcludePerformanceFees, isExcludeSubTotal);

  renderStaticHeader(data);
  renderPortfolioDetailFeeEstimate(data);
  renderFeeEstimateDetail(data);
  renderFeeEstimateProjectionMode(data);
  renderPortfolioComposition(data);
  renderSMASection(data);

  //Create worksheet
  const worksheet = utils.aoa_to_sheet(staticValues.workSheetRows);
  addStylesToWorkSheet(worksheet);

  resetRowsStaticData();

  return worksheet;
};

const renderSMASection = (data: FeeExportDTO) => {
  const { isFamilyLevel } = staticValues;

  if (!isFamilyLevel) return;

  const tableRows = convertToExcelSmaTable(
    data.smaPortfolioFee,
    staticValues.isExcludePerformanceFees,
  );

  const subProductIds = data.subProducts.map((subProduct) => subProduct.id);

  renderSMAPortfolioFee(tableRows, subProductIds);
  renderMultipleDataSection(data, tableRows, subProductIds);
  renderCodeForEachSMA(data, subProductIds);
};

const setStaticData = (
  data: FeeExportDTO,
  viewMode: FeesDisplayStyle = FeesDisplayStyle.Percentage,
  isExcludePerformanceFees: boolean,
  isExcludeSubTotal: boolean,
) => {
  const isAdviserUser =
    data.ownerType === UserType.AdviserAdviser ||
    data.ownerType === UserType.AdviserAdminAdviser;

  staticValues.adviserName = data.adviserName ?? "";
  staticValues.practiceName =
    (isAdviserUser ? data.firmName : data.practiceName) ?? "";
  staticValues.ownerName = data.ownerName ?? "";
  staticValues.firmName = data.firmName ?? "";
  staticValues.familyGroupName = data.familyGroupName ?? "";
  staticValues.ownerType = data.ownerType;
  staticValues.serialCode = data.serial ?? "";
  staticValues.isSalesUser = data.ownerType === UserType.SupplierBdmSale;
  staticValues.isAdviserUser = isAdviserUser;
  staticValues.isFamilyLevel =
    data.portfolioSetup === FeeCalculatorPortfolioLevel.Family;
  staticValues.isExcludePerformanceFees = isExcludePerformanceFees;
  staticValues.isDollarMode = viewMode === FeesDisplayStyle.Dollar;
  staticValues.isExcludeSubTotal = isExcludeSubTotal;

  staticValues.numOfItems = data.subProducts.length;
  staticValues.endColIndex = staticValues.numOfItems + 1;

  staticValues.hiddenSubProductIndexMap = data.hiddenIndexColumnMap;

  staticValues.hasMainProduct = data.subProducts.some(
    (item) => item.isProductMain,
  );

  staticValues.productNameCells = removeSubProductNameForSpecificProduct(
    data.subProducts,
  ).map(createProductNameCell);

  data.subProducts?.forEach((item) => {
    const key = `${item.id}-${item.rateCardVersion ?? 0}`;
    staticValues.productIdsMap[key] = item.productId;
  });

  staticValues.productRecommendations = data.productRecommendationsRecord || {};

  if (staticValues.endColIndex === 2) staticValues.onlyOneColData = true;
};

const resetRowsStaticData = () => {
  staticValues = new MSExcelDownloadStaticValues();
};

const setEndRowIndex = () => {
  staticValues.endRowIndex = staticValues.workSheetRows.length - 1;
};

const insertEmptyRow = () => {
  const arr = Array(staticValues.endColIndex + 1).fill("");
  staticValues.workSheetRows.push(arr);
};

const mergeCellsInRow = (
  rowIndex: number,
  startCol: number,
  endCol: number,
) => {
  const { workSheetMerges } = staticValues;
  workSheetMerges.push({
    s: {
      r: rowIndex,
      c: startCol,
    },
    e: {
      r: rowIndex,
      c: endCol,
    },
  });
};

const insertFillBlueRowIndex = (index: number) => {
  staticValues.fillBlueRowIndexes.push(index);
};

const insertFillLightBlueRowIndex = (index: number) => {
  staticValues.fillLightBlueRowIndexes.push(index);
};

const insertFillYellowRowIndex = (index: number) => {
  staticValues.fillYellowRowIndexes.push(index);
};

const insertFillLightYellowRowIndex = (index: number) => {
  staticValues.fillLightYellowRowIndexes.push(index);
};

const insertStyleTextBoldRowIndex = (index: number) => {
  staticValues.styleTextBoldRowIndexes.push(index);
};

const insertStyleBorderTopRowIndex = (index: number) => {
  staticValues.styleBorderTopRowIndexes.push(index);
};

const insertRowsHeighTrack = (index: number, value: number) => {
  staticValues.rowsHeightTrack.set(index, value);
};

const fillRowColor = ({
  workSheet,
  indexes,
  fillStyle,
}: {
  workSheet: WorkSheet;
  indexes: number[];
  fillStyle: { patternType: string; fgColor: { rgb: string } };
}) => {
  const { endColIndex } = staticValues;

  indexes.forEach((i) => {
    for (let col = 1; col <= endColIndex; col++) {
      const address = utils.encode_cell({ c: col, r: i });
      const cell: CellObject = workSheet[address] || {
        v: "",
        t: "s",
      };
      cell.s = { ...cell.s, fill: fillStyle };

      workSheet[address] = cell;
    }
  });
};

const styleRowTextBold = ({
  workSheet,
  indexes,
}: {
  workSheet: WorkSheet;
  indexes: number[];
}) => {
  const { endColIndex } = staticValues;
  indexes.forEach((i) => {
    for (let col = 1; col <= endColIndex; col++) {
      const address = utils.encode_cell({ c: col, r: i });
      const cell: CellObject = workSheet[address] || {
        v: "",
      };
      cell.s = {
        ...cell.s,
        font: {
          bold: true,
        },
      };

      workSheet[address] = cell;
    }
  });
};

const styleRowBorderTop = ({
  workSheet,
  indexes,
}: {
  workSheet: WorkSheet;
  indexes: number[];
}) => {
  const { endColIndex } = staticValues;
  indexes.forEach((i) => {
    for (let col = 1; col <= endColIndex; col++) {
      const address = utils.encode_cell({ c: col, r: i });
      const cell: CellObject = workSheet[address] || {
        v: "",
      };
      cell.s = {
        ...cell.s,
        border: {
          top: {
            style: "thin",
            color: "000000",
          },
        },
      };

      workSheet[address] = cell;
    }
  });
};

const addStylesToWorkSheet = (workSheet: WorkSheet) => {
  const {
    fillBlueRowIndexes,
    fillLightBlueRowIndexes,
    fillYellowRowIndexes,
    fillLightYellowRowIndexes,
    styleTextBoldRowIndexes,
    styleBorderTopRowIndexes,
  } = staticValues;

  // Fill row color
  const fillConfigs = [
    { indexes: fillBlueRowIndexes, fillStyle: fillBlueV2 },
    { indexes: fillLightBlueRowIndexes, fillStyle: fillLightBlue },
    { indexes: fillYellowRowIndexes, fillStyle: fillYellow },
    { indexes: fillLightYellowRowIndexes, fillStyle: fillLightYellow },
  ];

  fillConfigs.forEach(({ indexes, fillStyle }) => {
    fillRowColor({ workSheet, indexes, fillStyle });
  });

  styleRowTextBold({ workSheet, indexes: styleTextBoldRowIndexes });

  styleRowBorderTop({ workSheet, indexes: styleBorderTopRowIndexes });

  // Inti style cols
  const wscols = [{ wch: 2 }, { wch: 60 }, { wch: 18 }];
  Array.from({ length: staticValues.numOfItems - 1 }).forEach(() =>
    wscols?.push({ wch: 18 }),
  );

  //Styles
  workSheet["!cols"] = wscols;
  workSheet["!merges"] = staticValues.workSheetMerges;
  workSheet["!rows"] = staticValues.workSheetRows.map((_, index) => ({
    hpx: staticValues.rowsHeightTrack.get(index) ?? DefaultCellHeight,
  }));

  drawTableBorders(workSheet);
};

const drawTableBorders = (workSheet: WorkSheet) => {
  const { endColIndex, endRowIndex } = staticValues;

  const rangeToDraw: Range = {
    s: {
      c: 1,
      r: 1,
    },
    e: {
      c: endColIndex,
      r: endRowIndex,
    },
  };

  drawBorders(workSheet, rangeToDraw);
};

const drawBorders = (worksheet: WorkSheet, rangeToDraw?: Range) => {
  const range = rangeToDraw ?? utils.decode_range(worksheet["!ref"] ?? "");
  const borderStyle = { style: "medium", color: "000000" };

  const applyBorder = (cellAddress: string, border: string) => {
    const cell = worksheet[cellAddress] || { v: "", t: "s" };
    cell.s = cell.s || {};
    cell.s.border = {
      ...cell.s.border,
      [border]: borderStyle,
    };
    worksheet[cellAddress] = cell;
  };

  const applyBorders = (
    startRow: number,
    endRow: number,
    startCol: number,
    endCol: number,
    border: string,
  ) => {
    for (let row = startRow; row <= endRow; row++) {
      for (let col = startCol; col <= endCol; col++) {
        const cellAddress = utils.encode_cell({ r: row, c: col });
        applyBorder(cellAddress, border);
      }
    }
  };

  // Top border
  applyBorders(range.s.r, range.s.r, range.s.c, range.e.c, "top");

  // Bottom border
  applyBorders(range.e.r, range.e.r, range.s.c, range.e.c, "bottom");

  // Left border
  applyBorders(range.s.r, range.e.r, range.s.c, range.s.c, "left");

  // Right border
  applyBorders(range.s.r, range.e.r, range.e.c, range.e.c, "right");
};

const renderStaticHeader = (data: FeeExportDTO) => {
  const {
    workSheetRows,
    endColIndex,
    isSalesUser,
    isAdviserUser,
    onlyOneColData,
    adviserName,
    practiceName,
    firmName,
    familyGroupName,
  } = staticValues;

  insertEmptyRow();

  mergeCellsInRow(1, 1, endColIndex);
  insertFillBlueRowIndex(workSheetRows.length);
  insertRowsHeighTrack(
    workSheetRows.length,
    onlyOneColData ? TitleCellHeight * 2 : TitleCellHeight,
  );
  workSheetRows.push([
    "",
    {
      v: isAdviserUser
        ? ExcelLabelConstants.HeaderReview
        : ExcelLabelConstants.HeaderPlatformAnalysis,
      t: "s",
      s: {
        font: {
          bold: true,
          sz: 14,
        },
        alignment: {
          horizontal: "left",
          wrapText: onlyOneColData,
        },
      },
    },
  ]);

  const subTitle =
    isSalesUser && !isEmpty(adviserName) && !isEmpty(practiceName)
      ? ExcelSubTitle.Adviser(adviserName, practiceName)
      : ExcelSubTitle.Supplier(firmName);

  workSheetRows.push(["", subTitle]);

  insertEmptyRow();
  insertRowsHeighTrack(
    workSheetRows.length,
    calculateRowHeight(data.name, endColIndex),
  );
  mergeCellsInRow(workSheetRows.length, 2, endColIndex);
  workSheetRows.push([
    "",
    isAdviserUser ? "Suitability Review name:" : "Analysis name:",
    {
      v: data.name,
      t: "s",
      s: {
        alignment: { horizontal: "left", wrapText: onlyOneColData },
      },
    },
  ]);

  const label = isAdviserUser ? "Adviser:" : "Owner:";
  const str = isAdviserUser
    ? `${adviserName} of ${practiceName}`
    : `${data.ownerName} of ${data.supplierName}`;

  insertRowsHeighTrack(
    workSheetRows.length,
    calculateRowHeight(str, endColIndex),
  );
  mergeCellsInRow(workSheetRows.length, 2, endColIndex);
  workSheetRows.push([
    "",
    label,
    {
      v: str,
      t: "s",
      s: {
        alignment: { horizontal: "left", wrapText: onlyOneColData },
      },
    },
  ]);

  if (isAdviserUser) {
    workSheetRows.push([
      "",
      ExcelLabelConstants.FamilyGroup,
      `${familyGroupName}`,
    ]);
  }

  mergeCellsInRow(workSheetRows.length, 2, endColIndex);
  workSheetRows.push([
    "",
    ExcelLabelConstants.LastEdit,
    {
      v: data.lastModifiedDate ?? "",
      ...exportDateTime,
    },
  ]);

  mergeCellsInRow(workSheetRows.length, 2, endColIndex);
  workSheetRows.push([
    "",
    ExcelLabelConstants.DataValid,
    {
      v: data.databaseVersion ?? "",
      ...exportDate,
    },
  ]);

  setEndRowIndex();
};

const renderPortfolioDetailFeeEstimate = (data: FeeExportDTO) => {
  const { workSheetRows } = staticValues;

  insertEmptyRow();

  insertFillBlueRowIndex(workSheetRows.length);
  workSheetRows.push([
    "",
    {
      v: ExcelLabelConstants.PortfolioDetail,
      t: "s",
      s: {
        font: {
          bold: true,
        },
      },
    },
  ]);

  if (data.feePortfolioDetails?.idps?.length) {
    workSheetRows.push([
      "",
      ExcelLabelConstants.IDPSAccount,
      ...data.feePortfolioDetails.idps.map((value: number) => ({
        v: value,
        ...exportCurrency,
      })),
    ]);
  }

  if (data.feePortfolioDetails?.super?.length) {
    workSheetRows.push([
      "",
      ExcelLabelConstants.SuperAccount,
      ...data.feePortfolioDetails.super.map((value: number) => ({
        v: value,
        ...exportCurrency,
      })),
    ]);
  }

  if (data?.feePortfolioDetails?.familyMembers) {
    forEach(data?.feePortfolioDetails?.familyMembers, (item) => {
      pushFamilyMemberRow(
        item?.name ? item?.name + ":" : "",
        getStringFamilyMemberAccounts(item.familyMemberAccounts),
      );
    });
  }

  if (data.feePortfolioDetails?.totalPortfolioValue) {
    workSheetRows.push([
      "",
      {
        v: ExcelLabelConstants.TotalPortfolio,
        t: "s",
        s: {
          fill: fillYellow,
          font: {
            bold: true,
          },
        },
      },
      {
        v: data.feePortfolioDetails.totalPortfolioValue,
        ...exportCurrency,
        s: {
          fill: fillYellow,
          font: {
            bold: true,
            underline: true,
          },
          alignment: {
            horizontal: "left",
          },
        },
      },
    ]);
  }

  const {
    transactionsOutside,
    transactionsWithin,
    investments,
    northBalances,
    cfsBalances,
    expandBalances,
    cpsBalances,
  } = getAnalysisSummaryPortfolioDetails(data.feePortfolioDetails);

  if (cfsBalances.length) {
    pushArrayText(
      FeePortfolioDetailsConstants.BalanceInCFSEdgeMenus + ":",
      cfsBalances,
    );
  }

  if (expandBalances.length) {
    const isAllExpandEssentialsHidden = isAllExpandEssentialSubProductsHidden({
      selectedSubProducts: data.subProducts ?? [],
      hiddenSubProductIds: data.hiddenIds ?? [],
    });
    !isAllExpandEssentialsHidden &&
      pushArrayText(
        FeePortfolioDetailsConstants.BalanceInExpandEssentialMenus + ":",
        expandBalances,
      );
  }

  if (northBalances.length) {
    pushArrayText(
      FeePortfolioDetailsConstants.BalanceInNorthMenus + ":",
      northBalances,
    );
  }

  const retailInsurancePolicyInvestments = investments.filter((investment) =>
    investment.match(FeePortfolioDetailsConstants.RetailInsurancePoliciesRegex),
  );

  const hasNoPortfolioLevel =
    data.portfolioSetup === FeeCalculatorPortfolioLevel.No;

  if (hasNoPortfolioLevel && investments.length) {
    pushArrayText(
      FeePortfolioDetailsConstants.InvestmentsAndOtherHoldings + ":",
      investments,
    );
  }

  if (
    data?.isShowHoldingsNumber &&
    data.feePortfolioDetails?.totalDifferentInvestments
  ) {
    workSheetRows.push([
      "",
      FeePortfolioDetailsConstants.TotalDifferentInvestmentsHeld + ":",
      displayIntNumber(data.feePortfolioDetails.totalDifferentInvestments),
    ]);
  }

  if (transactionsOutside.length) {
    pushArrayText(
      FeePortfolioDetailsConstants.ManageAccountTransactionOutside + ":",
      transactionsOutside,
    );
  }

  if (transactionsWithin.length) {
    pushArrayText(
      FeePortfolioDetailsConstants.ManageAccountTransactionWithin + ":",
      transactionsWithin,
    );
  }

  if (retailInsurancePolicyInvestments.length) {
    pushArrayText(
      FeePortfolioDetailsConstants.NumberOfRetailInsurancePolices + ":",
      retailInsurancePolicyInvestments,
    );
  }

  setEndRowIndex();
};

const pushFamilyMemberRow = (label: string, arr: string) => {
  const { workSheetRows, endColIndex } = staticValues;

  const str = arr;
  insertRowsHeighTrack(
    workSheetRows.length,
    calculateRowHeight(str, endColIndex),
  );
  mergeCellsInRow(workSheetRows.length, 2, endColIndex);

  workSheetRows.push([
    "",
    {
      v: label,
      t: "s",
      s: {
        alignment: {
          vertical: "center",
        },
      },
    },
    {
      v: str,
      t: "s",
      s: {
        alignment: { vertical: "center", wrapText: true },
      },
    },
  ]);
};

const calculateRowHeight = (str: string, endColIndex: number) => {
  return (
    Math.ceil(str.length / (TotalCharacterInCell * (endColIndex - 1))) *
    DefaultCellHeight
  );
};

const pushArrayText = (label: string, arr: string[]) => {
  const { workSheetRows, workSheetMerges, endColIndex } = staticValues;

  const str = arr.join(", ");
  insertRowsHeighTrack(
    workSheetRows.length,
    calculateRowHeight(str, endColIndex),
  );
  workSheetMerges.push({
    s: {
      r: workSheetRows.length,
      c: 2,
    },
    e: {
      r: workSheetRows.length,
      c: endColIndex,
    },
  });
  workSheetRows.push([
    "",
    {
      v: label,
      t: "s",
      s: {
        alignment: {
          vertical: "center",
        },
      },
    },
    {
      v: str,
      t: "s",
      s: {
        alignment: { vertical: "center", wrapText: true },
      },
    },
  ]);
};

const renderFeeEstimateProjectionMode = (data: FeeExportDTO) => {
  const {
    workSheetRows,
    endColIndex,
    isAdviserUser,
    isExcludePerformanceFees,
    isDollarMode,
    isFamilyLevel,
    hasMainProduct,
    productNameCells,
  } = staticValues;

  //Display sub-product title
  insertEmptyRow();
  if (data.subProducts.length) {
    const subProducts = data.subProducts;
    insertStyleTextBoldRowIndex(workSheetRows.length);

    const title = isFamilyLevel
      ? !isExcludePerformanceFees
        ? HeatMapTableTitleConstants.ProjectionViewTitle
        : HeatMapTableTitleConstants.ProjectionViewExcPerfTitle
      : HeatMapTableTitleConstants.DefaultTitle;

    insertFillLightBlueRowIndex(workSheetRows.length);
    insertRowsHeighTrack(
      workSheetRows.length,
      hasMainProduct
        ? MSWorkSheetStyleConstants.MainProductNameRowHeight
        : MSWorkSheetStyleConstants.AnalysisNameRowHeight,
    );
    workSheetRows.push([
      "",
      { v: title, t: "s", s: { alignment: { wrapText: true } } },
      ...productNameCells,
    ]);

    //Display fee projection data
    forEach(data.subProducts[0].fees, (fee, index) => {
      workSheetRows.push([
        "",
        {
          v: fee.avgTotalPortfolio,
          ...exportCurrency,
          s: {
            alignment: {
              horizontal: "left",
            },
            font: {
              underline: !!fee.isCurrentPortfolio,
            },
          },
        },
        ...subProducts.map((subProduct: FeeSubProductDTO) =>
          isDollarMode
            ? ({
                v: isExcludePerformanceFees
                  ? subProduct.fees[index].totalCostExcPerfForDisplay ?? 0
                  : subProduct.fees[index].totalCostForDisplay,
                s: {
                  alignment: {
                    horizontal: "center",
                  },
                },
                ...exportCurrency,
              } as CellObject)
            : ({
                v:
                  (isExcludePerformanceFees
                    ? subProduct.fees[index]
                        .totalCostExcPerfForDisplayPercentage ?? 0
                    : subProduct.fees[index].totalCostForDisplayPercentage) /
                  100,
                s: {
                  alignment: {
                    horizontal: "center",
                  },
                },
                ...exportPercentage,
              } as CellObject),
        ),
      ]);
    });

    //Display notes and disclaimer are applied
    insertEmptyRow();
    const hasMissingInvestments = some(
      data.subProducts,
      (item) => !isEmpty(item?.warning),
    );
    if (hasMissingInvestments) {
      workSheetRows.push(["", ExcelLabelConstants.MissingInvestmentOption]);
    }
    workSheetRows.push([
      "",
      isAdviserUser
        ? ExcelLabelConstants.FeeReviewDisclaimer
        : ExcelLabelConstants.FeeAnalysisDisclaimer,
    ]);
    mergeCellsInRow(workSheetRows.length - 1, 1, endColIndex);

    setEndRowIndex();
  }
};

const renderPortfolioComposition = (data: FeeExportDTO) => {
  const {
    workSheetRows,
    isFamilyLevel,
    isExcludeSubTotal,
    hasMainProduct,
    productNameCells,
    productIdsMap,
    productRecommendations,
  } = staticValues;

  if (!isFamilyLevel) return;

  insertEmptyRow();
  insertFillBlueRowIndex(workSheetRows.length);
  workSheetRows.push([
    "",
    {
      v: data.portfolioCompositionByPlatform?.header,
      t: "s",
      s: {
        font: {
          bold: true,
        },
      },
    },
  ]);

  const subProducts = data.subProducts;
  insertRowsHeighTrack(workSheetRows.length, DefaultCellHeight * 2);
  insertFillLightBlueRowIndex(workSheetRows.length);
  insertStyleTextBoldRowIndex(workSheetRows.length);

  insertFillLightBlueRowIndex(workSheetRows.length);
  insertRowsHeighTrack(
    workSheetRows.length,
    hasMainProduct
      ? MSWorkSheetStyleConstants.MainProductNameRowHeight
      : MSWorkSheetStyleConstants.AnalysisNameRowHeight,
  );
  workSheetRows.push(["", "", ...productNameCells]);

  insertFillYellowRowIndex(workSheetRows.length);
  insertStyleTextBoldRowIndex(workSheetRows.length);
  workSheetRows.push([
    "",
    {
      v: ExcelLabelConstants.TotalPortfolioSize,
      s: {
        font: {
          bold: true,
        },
      },
    } as CellObject,
    ...subProducts.map((_) => ({
      v: data.portfolioCompositionByPlatform?.portfolioSize,
      s: {
        alignment: {
          horizontal: "center",
        },
      },
      ...exportCurrency,
    })),
  ]);

  let shouldShowDoubleAsteriskExplanation = false;
  const subProductIds = subProducts.map((subProduct) => subProduct.id);

  forEach(data.portfolioCompositionByPlatform?.sections, (section) => {
    forEach(section.rows, (row, rowIdx) => {
      if (row.isSubTotal && isExcludeSubTotal) {
        return;
      }

      if (rowIdx === 0) {
        insertFillLightYellowRowIndex(workSheetRows.length);
        insertStyleTextBoldRowIndex(workSheetRows.length);
        insertStyleBorderTopRowIndex(workSheetRows.length);
      }

      const mappedCells = row.cells.map((cell) => {
        const compiledDataSetupId = `${cell.setupId}${
          isNil(cell.rateCardVersion) ? "" : `|${cell.rateCardVersion}`
        }-${cell.rateCardVersion ?? 0}`;

        return { ...cell, productId: productIdsMap[compiledDataSetupId] };
      });

      const sortedCells = sortBySubProductOrder({
        data: mappedCells,
        productRecommendations,
        getProductId: (cell) => cell.productId || "",
      });

      const rowData = sortedCells.filter((data) => {
        const compiledDataSetupId = `${data.setupId}${
          isNil(data.rateCardVersion) ? "" : `|${data.rateCardVersion}`
        }`;

        return subProductIds.includes(compiledDataSetupId);
      });

      workSheetRows.push([
        "",
        row.name,
        ...rowData.map((item) => {
          shouldShowDoubleAsteriskExplanation =
            shouldShowDoubleAsteriskExplanation ||
            !!item.belowMinimumInvestmentSize;

          return renderCompositionCellValue(item);
        }),
      ]);
    });
  });
  insertStyleBorderTopRowIndex(workSheetRows.length);

  if (shouldShowDoubleAsteriskExplanation) {
    insertEmptyRow();
    workSheetRows.push(["", ExcelLabelConstants.BelowMinimumInvestmentSize]);
  }

  setEndRowIndex();
};

const renderFeeEstimateDetail = (data: FeeExportDTO) => {
  const {
    workSheetRows,
    isFamilyLevel,
    isDollarMode,
    isExcludePerformanceFees,
    onlyOneColData,
    endColIndex,
    hasMainProduct,
    productNameCells,
  } = staticValues;

  insertEmptyRow();

  const ratio = window.devicePixelRatio || 1;

  // Fee estimates title row
  insertFillBlueRowIndex(workSheetRows.length);
  insertStyleTextBoldRowIndex(workSheetRows.length);
  workSheetRows.push([
    "",
    {
      v: `Fee estimates: ${
        isFamilyLevel
          ? isExcludePerformanceFees
            ? HeatMapTableTitleConstants.DetailViewExcPerfTitle
            : HeatMapTableTitleConstants.DetailViewTitle
          : HeatMapTableTitleConstants.DefaultTitle
      }`,
      t: "s",
      s: {
        alignment: {
          wrapText: ratio === 1 && onlyOneColData && isExcludePerformanceFees,
        },
      },
    },
  ]);

  mergeCellsInRow(workSheetRows.length - 1, 1, endColIndex);

  if (
    isFamilyLevel &&
    isExcludePerformanceFees &&
    onlyOneColData &&
    ratio === 1
  ) {
    insertRowsHeighTrack(workSheetRows.length - 1, DefaultCellHeight * 2);
  }

  // Total fees header row
  insertFillLightBlueRowIndex(workSheetRows.length);
  insertStyleTextBoldRowIndex(workSheetRows.length);
  insertRowsHeighTrack(
    workSheetRows.length,
    hasMainProduct
      ? MSWorkSheetStyleConstants.MainProductNameRowHeight
      : MSWorkSheetStyleConstants.AnalysisNameRowHeight,
  );
  workSheetRows.push(
    createTotalFeesHeaderRow(
      isDollarMode,
      data.feePortfolioDetails?.totalPortfolioValue ?? 0,
      productNameCells,
    ),
  );

  // Total fees row
  insertFillYellowRowIndex(workSheetRows.length);
  insertStyleTextBoldRowIndex(workSheetRows.length);
  workSheetRows.push(createTotalFeesRow(data, isDollarMode, isFamilyLevel));

  // Platform fees row
  workSheetRows.push(createPlatformFeesRow(data, isDollarMode));

  // SMA fees row
  if (isFamilyLevel) {
    workSheetRows.push(createManageAccountFeesRow(data, isDollarMode));
  }

  insertEmptyRow();

  // Platform fees breakdown header rows
  insertFillLightBlueRowIndex(workSheetRows.length);
  insertStyleTextBoldRowIndex(workSheetRows.length);
  insertRowsHeighTrack(
    workSheetRows.length,
    hasMainProduct
      ? MSWorkSheetStyleConstants.MainProductNameRowHeight
      : MSWorkSheetStyleConstants.AnalysisNameRowHeight,
  );
  workSheetRows.push(createFeeBreakdownHeaderRow(productNameCells));

  // Platform fees breakdown rows
  data.subProducts[0].feeDetails.forEach((feeDetail, index) => {
    if (ExcludedFeeTypes.includes(feeDetail.name)) return;

    workSheetRows.push(
      createFeeBreakdownRow(data, feeDetail, index, isDollarMode),
    );

    const isLastIndex = index === data.subProducts[0].feeDetails.length - 1;

    if (
      isLastIndex &&
      feeDetail.name === FeeAnalysisLabelConstants.NetInterestEarnedOnCash
    ) {
      insertFillLightYellowRowIndex(workSheetRows.length - 1);
    }
  });

  setEndRowIndex();
};

const createProductNameCell = (subProduct: FeeSubProductDTO): CellObject => {
  const { isAdviserUser } = staticValues;

  return {
    v: `${
      !isEmpty(subProduct.name)
        ? `${subProduct.productName}\n${subProduct.name}`
        : `${subProduct.productName}`
    }${subProduct.warning ? "*" : ""}${
      subProduct.isProductMain
        ? `\n(${isAdviserUser ? "Current" : "Main"} Platform)`
        : ""
    }`,
    t: "s",
    s: {
      alignment: {
        horizontal: "center",
        vertical: "top",
        wrapText: true,
      },
    },
  };
};

const findFeeDetail = (feeDetails: FeeDetailDTO[], feeNames: string[]) => {
  return feeDetails.find((detail) => feeNames.includes(detail.name));
};

const calculatePlatformFee = (
  totalFee: FeeDetailDTO,
  smaFee: FeeDetailDTO | undefined,
) => ({
  ...totalFee,
  dollar: Math.round(totalFee.dollar ?? 0) - Math.round(smaFee?.dollar ?? 0),
  percentage:
    roundToTwoDecimals(totalFee.percentage ?? 0) -
    roundToTwoDecimals(smaFee?.percentage ?? 0),
});

const createTotalFeesHeaderRow = (
  isDollarMode: boolean,
  totalFee: number,
  productNameCells: CellObject[],
): MSWorkSheetRow => {
  const headerLabel = `${
    isDollarMode ? "$" : "%"
  } Fees for a total balance of ${displayCurrencyNumber(totalFee)}`;

  return ["", { v: headerLabel, t: "s" }, ...productNameCells];
};

const createTotalFeesRow = (
  data: FeeExportDTO,
  isDollarMode: boolean,
  isFamilyLevel: boolean,
): MSWorkSheetRow => {
  const label = isFamilyLevel
    ? FeeEstimatesTableLabelConstants.TotalPlatformAndSMAFees
    : FeeEstimatesTableLabelConstants.TotalPlatformFees;

  const feeCells = data.subProducts.flatMap((item) => {
    const totalFee = findFeeDetail(item.feeDetails, [
      FeeAnalysisLabelConstants.TotalFees,
      FeeAnalysisLabelConstants.TotalPlatformAndSMAFees,
    ]);

    return totalFee ? [renderCellValue(totalFee, isDollarMode)] : [];
  });

  return ["", { v: label, t: "s" }, ...feeCells];
};

const createPlatformFeesRow = (
  data: FeeExportDTO,
  isDollarMode: boolean,
): MSWorkSheetRow => {
  const feeCells = data.subProducts
    .map((item) => {
      const totalFee = findFeeDetail(item.feeDetails, [
        FeeAnalysisLabelConstants.TotalFees,
        FeeAnalysisLabelConstants.TotalPlatformAndSMAFees,
      ]);
      const smaFee = findFeeDetail(item.feeDetails, [
        FeeAnalysisLabelConstants.SMAFees,
      ]);

      if (!totalFee) return undefined;

      const platformFee = calculatePlatformFee(totalFee, smaFee);

      return renderCellValue(platformFee, isDollarMode);
    })
    .filter((cell): cell is CellObject => cell !== undefined);

  return [
    "",
    { v: FeeEstimatesTableLabelConstants.PlatformFees, t: "s" },
    ...feeCells,
  ];
};

const createManageAccountFeesRow = (
  data: FeeExportDTO,
  isDollarMode: boolean,
): MSWorkSheetRow => {
  const { workSheetRows, isExcludePerformanceFees } = staticValues;
  const feeCells = data.subProducts.flatMap((item) => {
    const smaFee = item.feeDetails.find((detail) => detail.isSmaFees);

    return smaFee ? [renderCellValue(smaFee, isDollarMode)] : [];
  });

  if (isExcludePerformanceFees) {
    insertRowsHeighTrack(workSheetRows.length, DefaultCellHeight * 2);
  }

  return [
    "",
    {
      v: isExcludePerformanceFees
        ? `${FeeEstimatesTableLabelConstants.ManagedAccountFeesExcPerf}`
        : `${FeeEstimatesTableLabelConstants.ManagedAccountFees}`,
      t: "s",
      s: {
        alignment: {
          wrapText: true,
        },
      },
    },
    ...feeCells,
  ];
};

const createFeeBreakdownHeaderRow = (
  productNameCells: CellObject[],
): MSWorkSheetRow => {
  return [
    "",
    { v: FeeEstimatesTableLabelConstants.PlatformFeeBreakdown, t: "s" },
    ...productNameCells,
  ];
};

const createFeeBreakdownRow = (
  data: FeeExportDTO,
  feeDetail: FeeDetailDTO,
  index: number,
  isDollarMode: boolean,
): MSWorkSheetRow => {
  const feeCells = data.subProducts.map((item) => {
    const fee = item.feeDetails[index];

    return renderCellValue(fee, isDollarMode);
  });

  return [
    "",
    { v: FeeEstimatesTableLabelMap[feeDetail.name], t: "s" },
    ...feeCells,
  ];
};

const renderCellValue = (
  feeDetail: FeeDetailDTO,
  isDollarMode: boolean,
): CellObject => {
  const { isExcludePerformanceFees } = staticValues;
  if (isNull(feeDetail.percentage) || isNull(feeDetail.dollar)) {
    return {
      v: "No data",
      t: "s",
      s: {
        alignment: {
          vertical: "center",
          horizontal: "center",
        },
      },
    };
  }

  return isDollarMode
    ? {
        v: isExcludePerformanceFees
          ? feeDetail.dollarExcPerf ?? 0
          : feeDetail?.dollar,
        s: {
          alignment: {
            vertical: "center",
            horizontal: "center",
          },
        },
        ...exportCurrency,
      }
    : {
        v:
          (isExcludePerformanceFees
            ? feeDetail.percentageExcPerf ?? 0
            : feeDetail?.percentage) / 100,
        s: {
          alignment: {
            vertical: "center",
            horizontal: "center",
          },
        },
        ...exportPercentage,
      };
};

const roundToTwoDecimals = (value: number) => {
  return Math.round(value * 100) / 100;
};

const renderCompositionCellValue = (
  item: PortfolioCompositionCellDTO,
): CellObject => {
  const value = parseFloat(item.value);
  const valueInPercent = parseFloat(item.valueInPercent);

  if (isNaN(value)) return getBasicTextCell({ cellValue: "-", tooltip: null });

  const valueFormatted = getNumberFormatValue(value);
  const valueInPercentFormatted = getNumberFormatValue(valueInPercent);

  const belowMinIndicator = item.belowMinimumInvestmentSize ? "**" : "";

  return {
    v: `$${valueFormatted} (${valueInPercentFormatted}%)${belowMinIndicator}`,
    t: "s",
    s: {
      alignment: {
        horizontal: "center",
      },
    },
  };
};

const getNumberFormatValue = (value: number) => {
  const valueFormatted = value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  if (valueFormatted.endsWith(".00")) {
    return valueFormatted.slice(0, -3);
  }

  return valueFormatted;
};

const ExcludedFeeTypes = [
  FeeAnalysisLabelConstants.TotalFees,
  FeeAnalysisLabelConstants.TotalPlatformAndSMAFees,
  FeeAnalysisLabelConstants.SMAFees,
  FeeAnalysisLabelConstants.SMAFeesExcPerf,
];

const renderSMAPortfolioFee = (
  tableRows: FeeAnalysisTableRow[],
  subProductIds: string[],
) => {
  const {
    workSheetRows,
    endColIndex,
    rowsHeightTrack,
    isDollarMode,
    isExcludePerformanceFees,
    serialCode,
    numOfItems,
    hasMainProduct,
    productNameCells,
  } = staticValues;

  const rowData: FeeAnalysisTableRow[] =
    tableRows?.map((row) => ({
      ...row,
      analysisData: row.analysisData?.filter((data) => {
        const compiledDataSetupId = `${data.subProductId}${
          data.setupType === AnalysisFeePortfolioSetupType.SubProduct
            ? ""
            : `|${data.rateCardVersion}`
        }`;

        return subProductIds.includes(compiledDataSetupId);
      }),
    })) ?? [];

  insertEmptyRow();

  // SMA portfolio title row
  insertFillBlueRowIndex(workSheetRows.length);
  mergeCellsInRow(workSheetRows.length, 1, endColIndex);
  insertStyleTextBoldRowIndex(workSheetRows.length);
  insertRowsHeighTrack(
    workSheetRows.length,
    numOfItems <= 2 ? DefaultCellHeight * 2 : DefaultCellHeight,
  );
  workSheetRows.push([
    "",
    {
      v: `Fee estimates: Managed account portfolio fees breakdown by platform (excludes ${
        isExcludePerformanceFees ? "performance fees and " : ""
      }non-managed account investments)`,
      t: "s",
      s: { alignment: { wrapText: true } },
    },
  ]);

  // Product names row
  insertFillLightBlueRowIndex(workSheetRows.length);
  rowsHeightTrack.set(
    workSheetRows.length,
    hasMainProduct
      ? MSWorkSheetStyleConstants.MainProductNameRowHeight
      : MSWorkSheetStyleConstants.AnalysisNameRowHeight,
  );
  insertStyleTextBoldRowIndex(workSheetRows.length);
  workSheetRows.push(["", "", ...productNameCells]);

  rowData.forEach((item) => {
    if (item.type === FeeAnalysisRowType.Section) {
      insertFillYellowRowIndex(workSheetRows.length);
      insertStyleTextBoldRowIndex(workSheetRows.length);
    }

    if (item.type === FeeAnalysisRowType.SubSection) {
      insertFillLightYellowRowIndex(workSheetRows.length);
      insertStyleTextBoldRowIndex(workSheetRows.length);
    }

    const getAnalysisData = () => {
      if (
        item.variableName === PortfolioFeesConstants.Total_fee &&
        isExcludePerformanceFees
      ) {
        return item.analysisData?.map((data) => ({
          ...data,
          value: data.valueExcPerf,
          valueInPercent: data.valueExcPerfInPercent,
        }));
      }

      return item.analysisData;
    };

    workSheetRows.push([
      "",
      item.name ?? "",
      ...getRowData(getAnalysisData(), item.variableName, isDollarMode),
    ]);
  });

  insertEmptyRow();

  // Information row
  mergeCellsInRow(workSheetRows.length, 1, endColIndex);
  workSheetRows.push(["", "^See web version for detailed breakdown"]);
  workSheetRows.push([
    "",
    "^^Cash fees not directly disclosed may have been incorporated within indirect or other management fees and costs",
  ]);
  mergeCellsInRow(workSheetRows.length, 1, 2);
  workSheetRows.push([
    "",
    "SMA fee analysis disclaimer and assumptions apply. See web version for details",
    ...Array(endColIndex - 2).fill(""),
    `Serial: ${serialCode}`,
  ]);

  setEndRowIndex();
};

const renderCodeForEachSMA = (data: FeeExportDTO, subProductIds: string[]) => {
  const {
    workSheetRows,
    rowsHeightTrack,
    productNameCellHeight,
    hiddenSubProductIndexMap,
    hasMainProduct,
    productNameCells,
    productIdsMap,
    productRecommendations,
  } = staticValues;

  insertFillBlueRowIndex(workSheetRows.length);
  insertStyleTextBoldRowIndex(workSheetRows.length);
  workSheetRows.push(["", "Code for each SMA by platform"]);

  insertFillLightBlueRowIndex(workSheetRows.length);
  insertStyleTextBoldRowIndex(workSheetRows.length);
  rowsHeightTrack.set(
    workSheetRows.length,
    hasMainProduct
      ? MSWorkSheetStyleConstants.MainProductNameRowHeight
      : MSWorkSheetStyleConstants.AnalysisNameRowHeight,
  );
  workSheetRows.push(["", "", ...productNameCells]);

  const rows =
    data.portfolioCompositionByPlatform?.platformCodes?.map((platformCode) => {
      const displayCells = platformCode.cells
        .filter((data) => {
          const compiledDataSetupId = `${data.id}${
            isNil(data.rateCardVersion) ? "" : `|${data.rateCardVersion}`
          }`;

          return subProductIds.includes(compiledDataSetupId);
        })
        ?.map((data) => {
          const compiledDataSetupId = `${data.id}${
            isNil(data.rateCardVersion) ? "" : `|${data.rateCardVersion}`
          }-${data.rateCardVersion ?? 0}`;

          return {
            ...data,
            productId: productIdsMap[compiledDataSetupId],
          };
        });

      const sortedCells = sortBySubProductOrder({
        data: displayCells,
        productRecommendations,
        getProductId: (cell) => cell.productId || "",
      });

      return { ...platformCode, cells: sortedCells };
    }) || [];

  rows.forEach((row) => {
    workSheetRows.push([
      "",
      row.name,
      ...row.cells.map<CellObject>((cell) =>
        getBasicTextCell({ cellValue: cell.platformCode, tooltip: null }),
      ),
    ]);
  });

  setEndRowIndex();
};

type CellProcessorProps = {
  cellValue: string;
  cellValuePercentage?: string;
  tooltip: MAAnalysisTooltipDTO | null;
  isDollarMode?: boolean;
  variableName?: string;
  isMultiple?: boolean;
  shouldExcludePerformance?: boolean;
};

const getBasicTextCell = ({
  cellValue,
  tooltip,
}: CellProcessorProps): CellObject => {
  const showCaret =
    cellValue !== ManagedAccountFieldTypeValue.Multiple &&
    (tooltip?.additionalText || tooltip?.data || tooltip?.header);

  const currentCellValue = `${cellValue}${showCaret ? "^" : ""}`;

  return {
    v: currentCellValue,
    t: "s",
    s: {
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
    },
  };
};

const getNumberCell = ({
  cellValue,
  tooltip,
}: CellProcessorProps): CellObject => {
  const showCaret =
    cellValue !== ManagedAccountFieldTypeValue.Multiple &&
    (tooltip?.additionalText || tooltip?.data || tooltip?.header);

  const currentCellValue = `$${cellValue}${showCaret ? "^" : ""}`;

  if (showCaret) {
    return {
      v: currentCellValue,
      t: "s",
      s: {
        alignment: { horizontal: "center", vertical: "center", wrapText: true },
      },
    };
  }

  return {
    v: cellValue,
    s: {
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
    },
    ...exportCurrency,
  };
};

const getPercentageCell = ({
  cellValue,
  tooltip,
}: CellProcessorProps): CellObject => {
  const showCaret =
    cellValue !== ManagedAccountFieldTypeValue.Multiple &&
    (tooltip?.additionalText || tooltip?.data || tooltip?.header);

  if (showCaret) {
    const stringValue = `${cellValue}%^`;

    return {
      v: stringValue,
      t: "s",
      s: {
        alignment: { horizontal: "center", vertical: "center", wrapText: true },
      },
    };
  }

  const numericValue = Number(cellValue);

  if (isNaN(numericValue)) {
    return {
      v: cellValue,
      t: "s",
      s: {
        alignment: { horizontal: "center", vertical: "center", wrapText: true },
      },
    };
  }

  return {
    v: parseFloat(cellValue) / 100,
    s: {
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
    },
    ...exportPercentage,
  };
};

const getYesNoCell = ({
  cellValue,
  tooltip,
  variableName,
}: CellProcessorProps): CellObject => {
  if (cellValue === ManagedAccountFieldTypeValue.True) {
    if (variableName === "Model_inception") {
      return getBasicTextCell({ cellValue: "Yes", tooltip });
    }

    return getBasicTextCell({ cellValue: "Yes", tooltip: null });
  }

  if (cellValue === ManagedAccountFieldTypeValue.False) {
    return getBasicTextCell({ cellValue: "No", tooltip: null });
  }

  if (cellValue === ManagedAccountFieldTypeValue.Multiple) {
    return getBasicTextCell({ cellValue: "Multiple", tooltip });
  }

  return getBasicTextCell({ cellValue, tooltip });
};

const getYesNoTextCell = ({
  cellValue,
  tooltip,
}: CellProcessorProps): CellObject => {
  if (cellValue === ManagedAccountFieldTypeValue.True) {
    return getBasicTextCell({ cellValue: "Net", tooltip: null });
  }

  if (cellValue === ManagedAccountFieldTypeValue.False) {
    return getBasicTextCell({ cellValue: "Gross", tooltip: null });
  }

  if (cellValue === ManagedAccountFieldTypeValue.Multiple) {
    return getBasicTextCell({ cellValue: "Multiple", tooltip });
  }

  return getBasicTextCell({ cellValue, tooltip });
};

const getDateTimeCell = ({
  cellValue,
  tooltip,
}: CellProcessorProps): CellObject => {
  const basicCell = getBasicTextCell({ cellValue, tooltip });
  const parsedDate = parse(cellValue, MSDefaultDateFormat, new Date());

  if (isNaN(parsedDate.getTime())) return basicCell;

  basicCell.v = parsedDate;
  basicCell.t = "d";
  basicCell.z = MSDefaultDateFormat;

  return basicCell;
};

const getWarningCell = ({
  cellValue,
  tooltip,
}: CellProcessorProps): CellObject => {
  if (cellValue === ManagedAccountFieldTypeValue.NoBreach) {
    return getBasicTextCell({ cellValue: "Yes", tooltip: null });
  }

  if (cellValue === ManagedAccountFieldTypeValue.Empty) {
    return getBasicTextCell({ cellValue: "-", tooltip: null });
  }

  return getBasicTextCell({ cellValue: "Breach", tooltip });
};

const getDropdownCell = ({
  cellValue,
  tooltip,
}: CellProcessorProps): CellObject => {
  if (cellValue === ManagedAccountFieldTypeValue.Multiple) {
    return getBasicTextCell({ cellValue, tooltip });
  }

  return getBasicTextCell({ cellValue, tooltip: null });
};

const getTextCell = ({
  cellValue,
  tooltip,
  isMultiple,
}: CellProcessorProps): CellObject => {
  if (isMultiple && tooltip) {
    return getBasicTextCell({ cellValue, tooltip });
  }

  return getBasicTextCell({ cellValue, tooltip: null });
};

const getCurrencyCell = ({
  cellValue,
  cellValuePercentage,
  tooltip,
  isDollarMode,
}: CellProcessorProps): CellObject => {
  if (tooltip && cellValue === ManagedAccountFieldTypeValue.Multiple) {
    return getBasicTextCell({
      cellValue: ManagedAccountFieldTypeValue.Multiple,
      tooltip: null,
    });
  }

  if (!isNumber(parseFloat(cellValue ?? ""))) {
    return getBasicTextCell({
      cellValue,
      tooltip,
    });
  }

  const numberDollar = parseFloat(cellValue ?? "0");
  const numberPercentage = parseFloat(cellValuePercentage ?? "0");

  if (isNaN(numberDollar) || isNaN(numberPercentage)) {
    return getBasicTextCell({ cellValue: "-", tooltip: null });
  }

  if (isDollarMode || !cellValuePercentage) {
    const negativeValue = numberDollar < 0;
    const dollarValue = Math.abs(numberDollar);
    const shouldShowDecimal = Math.ceil(dollarValue) / dollarValue > 1;
    const data = Intl.NumberFormat(
      undefined,
      shouldShowDecimal
        ? {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }
        : undefined,
    ).format(dollarValue);

    if (negativeValue) {
      return getBasicTextCell({
        cellValue: `${negativeValue && "-"}$${data}`,
        tooltip,
      });
    }

    return getNumberCell({
      cellValue: `${dollarValue}`,
      tooltip,
    });
  }

  return getPercentageCell({ cellValue: numberPercentage.toString(), tooltip });
};

const GetCellDataFunctionMap: Record<
  ManagedAccountFieldType,
  (props: CellProcessorProps) => CellObject
> = {
  [ManagedAccountFieldType.DateTime]: getDateTimeCell,
  [ManagedAccountFieldType.Percentage]: getPercentageCell,
  [ManagedAccountFieldType.Currency]: getCurrencyCell,
  [ManagedAccountFieldType.YesNo]: getYesNoCell,
  [ManagedAccountFieldType.Dropdown]: getDropdownCell,
  [ManagedAccountFieldType.YesNoText]: getYesNoTextCell,
  [ManagedAccountFieldType.Multiple]: getBasicTextCell,
  [ManagedAccountFieldType.Warning]: getWarningCell,
  [ManagedAccountFieldType.Text]: getTextCell,
};

const getRowData = (
  analysisData: MAAnalysisDataDTO[] | undefined,
  variableName: string | undefined,
  isDollarMode: boolean,
): CellObject[] => {
  const cells: CellObject[] = [];

  if (!analysisData) return cells;

  for (const data of analysisData) {
    if (data.value === ManagedAccountFieldTypeValue.NotDisclosed) {
      if (variableName === "CM_fee") {
        cells.push(
          getBasicTextCell({
            cellValue: `${data.value}^^`,
            tooltip: null,
          }),
        );
        continue;
      }

      cells.push(
        getBasicTextCell({
          cellValue: data.value,
          tooltip: null,
        }),
      );
      continue;
    }

    const cellProcessor =
      GetCellDataFunctionMap[data.fieldTypeId as ManagedAccountFieldType] ||
      getBasicTextCell;

    cells.push(
      cellProcessor({
        cellValue: data.value ?? "",
        cellValuePercentage: data.valueInPercent,
        tooltip: data.tooltip,
        isMultiple: data.isMultiple,
        variableName,
        isDollarMode,
      }),
    );
  }

  return cells;
};

const getExcludedPerformanceFeesSections = ({
  smaPortfolioFee,
  excludePerformanceFees,
}: {
  smaPortfolioFee: ManagedAccountFeeAnalysisContainerDTO | undefined;
  excludePerformanceFees: boolean;
}): MAAnalysisSectionDTO[] | undefined => {
  const originalSections = smaPortfolioFee?.sections;
  if (!excludePerformanceFees || !originalSections) return originalSections;

  return originalSections.map((section) => {
    const transformedSubSections = section.subSections?.map((subSection) => {
      if (
        !PortfolioFeesConstants.ExcludingPerformanceFeeVariableNames.includes(
          subSection.variableName ?? "",
        )
      ) {
        return subSection;
      }

      const transformedAnalysisData = subSection.analysisData?.map((data) => ({
        ...data,
        value: "-",
        valueInPercent: "-",
      }));

      return {
        ...subSection,
        analysisData: transformedAnalysisData,
        breakdowns: [
          {
            ...new MAAnalysisBreakdownDTO(),
            name: "Performance fees excluded from analysis",
            analysisData: transformedAnalysisData,
          },
        ],
      } as MAAnalysisSectionDTO;
    });

    return {
      ...section,
      subSections: transformedSubSections,
    };
  });
};

const convertToExcelSmaTable = (
  smaPortfolioFee: ManagedAccountFeeAnalysisContainerDTO | undefined,
  isExcludePerformanceFees: boolean,
) => {
  const { productIdsMap, productRecommendations } = staticValues;

  const { rows } = flatManagedAccountFeeAnalysisData({
    sections: getExcludedPerformanceFeesSections({
      smaPortfolioFee,
      excludePerformanceFees: isExcludePerformanceFees,
    }),
    isVariableHidden: () => false,
  });

  return rows.map((row) => {
    const { analysisData } = row;

    const mappedAnalysisData = analysisData?.map((data) => {
      const compiledDataSetupId = `${data.subProductId}${
        data.setupType === AnalysisFeePortfolioSetupType.SubProduct
          ? ""
          : `|${data.rateCardVersion}`
      }-${data.rateCardVersion ?? 0}`;

      return { ...data, productId: productIdsMap[compiledDataSetupId] };
    });

    return {
      ...row,
      analysisData: sortBySubProductOrder({
        data: mappedAnalysisData || [],
        productRecommendations,
        getProductId: (data) => data.productId || "",
      }),
    };
  });
};

const renderMultipleDataSection = (
  data: FeeExportDTO,
  tableRows: FeeAnalysisTableRow[],
  subProductIds: string[],
) => {
  const {
    workSheetRows,
    rowsHeightTrack,
    productNameCellHeight,
    hiddenSubProductIndexMap,
    hasMainProduct,
    productNameCells,
    productIdsMap,
    productRecommendations,
  } = staticValues;

  const sectionRows: FeeAnalysisTableRow[] =
    tableRows?.map((row) => {
      const filteredAnalysisData = row.analysisData
        ?.filter((data) => {
          const compiledDataSetupId = `${data.subProductId}${
            data.setupType === AnalysisFeePortfolioSetupType.SubProduct
              ? ""
              : `|${data.rateCardVersion}`
          }`;

          return subProductIds.includes(compiledDataSetupId);
        })
        ?.map((data) => {
          const compiledDataSetupId = `${data.subProductId}${
            data.setupType === AnalysisFeePortfolioSetupType.SubProduct
              ? ""
              : `|${data.rateCardVersion}`
          }-${data.rateCardVersion ?? 0}`;

          return {
            ...data,
            productId: productIdsMap[compiledDataSetupId],
          };
        });

      const sortedData = sortBySubProductOrder({
        data: filteredAnalysisData || [],
        productRecommendations,
        getProductId: (data) => data.productId || "",
      });

      return {
        ...row,
        analysisData: sortedData,
      };
    }) ?? [];

  const rows = data.portfolioCompositionByPlatform
    ? data.portfolioCompositionByPlatform.sections
        .filter((section) => section.rows.some((row) => row.id === "MA_total"))
        .flatMap((section) => section.rows.filter((row) => !row.isSubTotal))
    : [];

  const filteredRows = rows.filter((row) =>
    row.cells.some((cell) => cell.value !== "-"),
  );

  const sortedFilteredRows = filteredRows?.map((row) => {
    const mappedCells = row.cells
      ?.filter((data) => {
        const compiledDataSetupId = `${data.setupId}${
          isNil(data.rateCardVersion) ? "" : `|${data.rateCardVersion}`
        }`;

        return subProductIds.includes(compiledDataSetupId);
      })
      ?.map((cell) => {
        const compiledDataSetupId = `${cell.setupId}${
          isNil(cell.rateCardVersion) ? "" : `|${cell.rateCardVersion}`
        }-${cell.rateCardVersion ?? 0}`;

        return { ...cell, productId: productIdsMap[compiledDataSetupId] };
      });

    const sortedCells = sortBySubProductOrder({
      data: mappedCells,
      productRecommendations,
      getProductId: (cell) => cell.productId || "",
    });

    return {
      ...row,
      cells: sortedCells,
    };
  });

  sectionRows.forEach((section) => {
    const hasMultipleValue = section.analysisData?.some(
      (data) => data.value === ManagedAccountFieldTypeValue.Multiple,
    );
    const shouldShowMultipleSection =
      PortfolioFeesConstants.ShouldShowMultipleSectionVariableNames.includes(
        section.variableName ?? "",
      );

    if (hasMultipleValue && shouldShowMultipleSection) {
      insertFillBlueRowIndex(workSheetRows.length);
      insertStyleTextBoldRowIndex(workSheetRows.length);
      workSheetRows.push([
        "",
        `${section.name ?? ""} for each SMA by platform`,
      ]);

      insertFillLightBlueRowIndex(workSheetRows.length);
      insertStyleTextBoldRowIndex(workSheetRows.length);
      rowsHeightTrack.set(
        workSheetRows.length,
        hasMainProduct
          ? MSWorkSheetStyleConstants.MainProductNameRowHeight
          : MSWorkSheetStyleConstants.AnalysisNameRowHeight,
      );
      workSheetRows.push(["", "", ...productNameCells]);

      const analysisData = section.analysisData;

      sortedFilteredRows.forEach((row) => {
        workSheetRows.push([
          "",
          row.name,
          ...row.cells.map<CellObject>((cell, index) => {
            if (cell.value === "-") {
              return getBasicTextCell({ cellValue: "-", tooltip: null });
            }

            if (
              analysisData &&
              analysisData[index] &&
              !analysisData[index].tooltip
            ) {
              const cellProcessor =
                GetCellDataFunctionMap[
                  analysisData[index].fieldTypeId as ManagedAccountFieldType
                ] || getBasicTextCell;

              return cellProcessor({
                cellValue: analysisData[index].value ?? "",
                tooltip: null,
              });
            }

            const tooltip = analysisData?.[index]?.tooltip?.data.find(
              (d) => d.name === row.name,
            );

            const cellProcessor =
              GetCellDataFunctionMap[
                tooltip?.fieldType as ManagedAccountFieldType
              ] || getBasicTextCell;

            return cellProcessor({
              cellValue: tooltip?.value ?? "",
              tooltip: null,
            });
          }),
        ]);
      });
    }
  });

  setEndRowIndex();
};
